import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  UsersIcon, 
  DocumentDuplicateIcon, 
  ShoppingCartIcon, 
  DocumentIcon, 
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import Sidebar from './Sidebar';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { settings, loading: settingsLoading, error: settingsError } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Effect to update document title based on settings
  useEffect(() => {
    const baseTitle = 'Система управления рекламным агентством';
    let title = baseTitle; // Default title

    if (settingsLoading) {
      title = `Загрузка... | ${baseTitle}`;
    } else if (settingsError) {
      title = `Ошибка | ${baseTitle}`;
    } else if (settings?.name && settings.name !== 'Название не задано') { 
      // Prepend company name if it exists and is not the default placeholder
      title = `${settings.name} | ${baseTitle}`;
    }
    // Otherwise, keep the baseTitle

    document.title = title;

  }, [settings, settingsLoading, settingsError]); // Re-run when settings state changes
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div className="min-h-dvh flex flex-col">
      {/* Header */}
      <header className="bg-white shadow z-30 fixed top-0 left-0 right-0 h-16 flex items-center">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                className="block md:hidden mr-2 text-gray-600 hover:text-gray-900"
                onClick={toggleMobileMenu}
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
              <div className={`hidden md:block`}>
                <h1 className="text-xl font-bold text-gray-900 ">
                  {settingsLoading 
                    ? 'Загрузка...'
                    : settingsError
                      ? 'Ошибка загрузки настроек'
                      : settings?.name
                        ? `Система управления рекламным агентством ${settings.name}`
                        : 'Система управления рекламным агентством'
                  }
                </h1>
              </div>
              <div className="md:hidden">
                <h1 className="text-lg font-bold text-gray-900 ">
                  {settings?.name || 'Система управления'}
                </h1>
              </div>
            </div>
            <div className="flex items-center">
              {user && (
                <div className="flex items-center">
                  <span className="hidden sm:inline text-sm text-gray-700 mr-4">
                    {user.name || user.username} ({user.username})
                  </span>
                  <button
                    onClick={handleLogout}
                    className="flex items-center text-gray-600 hover:text-gray-900"
                    title="Выйти"
                  >
                    <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                    <span className="sr-only sm:not-sr-only sm:ml-1">Выйти</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 pt-16">
        <Sidebar 
          isOpen={isMobileMenuOpen} 
          isCollapsed={isSidebarCollapsed} 
          toggleCollapse={toggleSidebarCollapse}
        />

        {/* Conditionally apply padding based on route and add overflow-x-hidden and overflow-hidden */}
        <main className={`flex-1 bg-gray-50 transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'} ${location.pathname === '/whatsapp' ? '' : 'p-4 md:p-8'} overflow-x-hidden overflow-hidden`}> {/* Added overflow-hidden */}
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
